/**
 * A community folder's files (gh #5003, Workspace layout): `community.yaml` — identity, settings,
 * the join link's state, the rooms and the course folders in order — `rules.md`, the rules members
 * see, and the course folders under `courses/` (`course-files.ts`).
 *
 * Workspace files are untrusted input (Decision 10): every read and write goes through the
 * no-follow helpers of `lib/fs-safety.ts`, and a file is checked against the `@wayai/core` bodies
 * and the shape below before any request (Rules: Validation first). Every problem is reported at
 * once, so one run names all of them.
 *
 * Write-back is canonical, as pull writes hub and base files: the YAML is re-emitted in a fixed
 * key order and comments are not kept.
 */

import * as path from 'node:path';
import * as yaml from 'js-yaml';
import { communityRoomCreateBody, communitySettingsBody, type CommunityRoomKind } from '@wayai/core/contracts';
import { expected } from '../lib/expected.js';
import { PATH_SAFE_ID_RULE, isPathSafeId } from '../lib/base-id.js';
import { readRealFileOrThrow, writeFileIfChangedNoFollow } from '../lib/fs-safety.js';
import { YAML_DUMP_OPTIONS } from '../lib/yaml-writer.js';
import { COMMUNITY_FILE, RULES_FILE } from '../lib/community-workspace.js';
import { UUID_RE } from '../lib/utils.js';
import { toLf } from './model.js';
import {
  contractProblem,
  isMapping,
  optionalString,
  readCourseFolders,
  readIdList,
  readNameList,
  unknownKeys,
  writeCourseFolder,
  type CourseFolder,
  type Unlisted,
} from './course-files.js';

/** One room in `community.yaml`. No `id` yet: push creates it. */
export interface RoomEntry {
  /** The stable local name: chosen once, kept through renames; the `client_id` derives from it. */
  key: string;
  id?: string;
  rev?: string;
  name: string;
  /** Read-only once the room exists. */
  kind: CommunityRoomKind;
  archived: boolean;
}

export interface CommunityFiles {
  /** Read-only: which community the folder is. */
  community_id: string;
  /** Read-only: there is no rename. */
  name: string;
  settings: { rev?: string; slow_mode_seconds: number; courses_label: string };
  /** `rules.md`, line endings as LF; '' for none (and for no file). */
  rules: string;
  join_link_enabled: boolean;
  join_link_rev?: string;
  rooms: RoomEntry[];
  /** `courses:` — the course folders, in display order. */
  courses: CourseFolder[];
  /** `synced.courses:` — the courses' ids in the server's order as of the last sync: their order's base. */
  synced_courses?: string[];
  /** Folders in `courses/` that `courses:` does not list. */
  unlistedCourses: Unlisted[];
}

const TOP_KEYS = ['community_id', 'name', 'settings', 'join_link_enabled', 'join_link_rev', 'rooms', 'courses', 'synced'];
const SETTINGS_KEYS = ['rev', 'slow_mode_seconds', 'courses_label'];
const ROOM_KEYS = ['key', 'id', 'rev', 'name', 'kind', 'archived'];

/**
 * Read and check a community folder, before any request. Throws one expected error naming every
 * problem found; a symlinked file is refused outright.
 */
export function readCommunityFolder(folder: string): CommunityFiles {
  const where = path.basename(folder);
  const yamlPath = path.join(folder, COMMUNITY_FILE);
  const text = readRealFileOrThrow(folder, yamlPath);
  if (text === null) {
    throw expected(`No ${COMMUNITY_FILE} in ${where}/: pull the community first (wayai pull communities/<community_id>).`);
  }
  const rules = toLf(readRealFileOrThrow(folder, path.join(folder, RULES_FILE)) ?? '');

  let doc: unknown;
  try {
    doc = yaml.load(text);
  } catch (err) {
    throw expected(`${where}/${COMMUNITY_FILE} is not valid YAML: ${err instanceof Error ? err.message : String(err)}`);
  }
  if (!isMapping(doc)) throw expected(`${where}/${COMMUNITY_FILE} must be a YAML mapping.`);

  const problems: string[] = [];
  unknownKeys(doc, TOP_KEYS, COMMUNITY_FILE, problems);

  const communityId = doc.community_id;
  if (typeof communityId !== 'string' || !UUID_RE.test(communityId)) {
    problems.push(`${COMMUNITY_FILE}: community_id must be the community's id (a UUID)`);
  }
  if (typeof doc.name !== 'string') problems.push(`${COMMUNITY_FILE}: name must be text`);

  const settings = isMapping(doc.settings) ? doc.settings : null;
  if (!settings) problems.push(`${COMMUNITY_FILE}: settings must be a mapping`);
  else unknownKeys(settings, SETTINGS_KEYS, `${COMMUNITY_FILE} settings`, problems);
  const settingsRev = settings ? optionalString(settings, 'rev', `${COMMUNITY_FILE} settings`, problems) : undefined;
  const slowMode = settings?.slow_mode_seconds;
  const coursesLabel = settings?.courses_label ?? '';
  if (settings && typeof slowMode !== 'number') problems.push(`${COMMUNITY_FILE} settings: slow_mode_seconds must be a number`);
  if (typeof coursesLabel !== 'string') problems.push(`${COMMUNITY_FILE} settings: courses_label must be text`);
  if (settings && typeof slowMode === 'number' && typeof coursesLabel === 'string') {
    const parsed = communitySettingsBody.safeParse({ rules, slow_mode_seconds: slowMode, courses_label: coursesLabel });
    if (!parsed.success) problems.push(`settings: ${contractProblem(parsed.error)}`);
  }

  if (typeof doc.join_link_enabled !== 'boolean') problems.push(`${COMMUNITY_FILE}: join_link_enabled must be true or false`);
  const joinLinkRev = optionalString(doc, 'join_link_rev', COMMUNITY_FILE, problems);

  const rooms = readRooms(doc.rooms, problems);
  const synced = readCommunitySynced(doc.synced, problems);
  const { courses, unlisted } = readCourseFolders(folder, readNameList(doc.courses, `${COMMUNITY_FILE} courses`, 'course folder', problems), problems);
  problems.push(...crossFileProblems(rooms, courses));

  if (problems.length > 0) {
    throw expected(`${where}/ cannot be synced:\n${problems.map((p) => `  - ${p}`).join('\n')}`);
  }
  return {
    community_id: communityId as string,
    name: doc.name as string,
    settings: {
      ...(settingsRev === undefined ? {} : { rev: settingsRev }),
      slow_mode_seconds: slowMode as number,
      courses_label: coursesLabel as string,
    },
    rules,
    join_link_enabled: doc.join_link_enabled as boolean,
    ...(joinLinkRev === undefined ? {} : { join_link_rev: joinLinkRev }),
    rooms,
    courses,
    ...(synced === undefined ? {} : { synced_courses: synced }),
    unlistedCourses: unlisted,
  };
}

/** `synced:` — the courses' order as of the last sync. */
function readCommunitySynced(raw: unknown, problems: string[]): string[] | undefined {
  if (raw === undefined || raw === null) return undefined;
  if (!isMapping(raw)) {
    problems.push(`${COMMUNITY_FILE}: synced must be a mapping`);
    return undefined;
  }
  unknownKeys(raw, ['courses'], `${COMMUNITY_FILE} synced`, problems);
  return readIdList(raw.courses, `${COMMUNITY_FILE} synced.courses`, problems);
}

/**
 * What no one file shows: each course, module and lesson `id` once in the whole community (a copied
 * folder, entry or file), and every lesson's `room` one of the rooms' keys.
 */
function crossFileProblems(rooms: RoomEntry[], courses: CourseFolder[]): string[] {
  const problems: string[] = [];
  const seen = new Map<string, string>();
  const once = (id: string | undefined, where: string) => {
    if (id === undefined) return;
    const first = seen.get(id);
    if (first) problems.push(`${where}: id ${id} is also ${first}'s — a copy keeps its original's id; remove the id (and its revs) to make a new one`);
    else seen.set(id, where);
  };
  const roomKeys = new Set(rooms.map((room) => room.key));
  for (const course of courses) {
    once(course.id, `course ${course.slug}`);
    for (const module of course.modules) {
      once(module.id, `module ${course.slug}/${module.key}`);
      for (const lesson of module.lessons) {
        once(lesson.id, `lesson ${course.slug}/${lesson.slug}`);
        if (lesson.room !== undefined && !roomKeys.has(lesson.room)) {
          problems.push(`lesson ${course.slug}/${lesson.slug}: room ${lesson.room} is not a room key in ${COMMUNITY_FILE}`);
        }
      }
    }
  }
  return problems;
}

/** The rooms list: each key and id once, keys plain names, every name and kind a valid create. */
function readRooms(raw: unknown, problems: string[]): RoomEntry[] {
  if (raw === undefined || raw === null) return [];
  if (!Array.isArray(raw)) {
    problems.push(`${COMMUNITY_FILE}: rooms must be a list`);
    return [];
  }
  const rooms: RoomEntry[] = [];
  const keys = new Set<string>();
  const ids = new Set<string>();
  raw.forEach((entry, index) => {
    const where = `${COMMUNITY_FILE} rooms[${index}]`;
    if (!isMapping(entry)) {
      problems.push(`${where}: must be a mapping`);
      return;
    }
    unknownKeys(entry, ROOM_KEYS, where, problems);
    const key = entry.key;
    if (typeof key !== 'string' || !isPathSafeId(key)) {
      problems.push(`${where}: key must be a plain name — ${PATH_SAFE_ID_RULE}`);
      return;
    }
    const at = `${COMMUNITY_FILE} room ${key}`;
    if (keys.has(key)) problems.push(`${at}: the key is used twice`);
    keys.add(key);
    const id = optionalString(entry, 'id', at, problems);
    const rev = optionalString(entry, 'rev', at, problems);
    if (id !== undefined) {
      if (!UUID_RE.test(id)) problems.push(`${at}: id must be a room id (a UUID)`);
      else if (ids.has(id)) problems.push(`${at}: id ${id} is listed twice`);
      ids.add(id);
    } else if (rev !== undefined) {
      problems.push(`${at}: a rev without an id — remove the rev to create the room`);
    }
    if (entry.archived !== undefined && typeof entry.archived !== 'boolean') {
      problems.push(`${at}: archived must be true or false`);
    }
    const parsed = communityRoomCreateBody.safeParse({ kind: entry.kind, name: entry.name });
    if (!parsed.success) {
      problems.push(`${at}: ${contractProblem(parsed.error)}`);
      return;
    }
    rooms.push({
      key,
      ...(id === undefined ? {} : { id }),
      ...(rev === undefined ? {} : { rev }),
      name: entry.name as string,
      kind: parsed.data.kind,
      archived: entry.archived === true,
    });
  });
  return rooms;
}

/** `community.yaml` as written: a fixed key order, revs and ids only where known. */
export function renderCommunityYaml(files: CommunityFiles): string {
  const doc = {
    community_id: files.community_id,
    name: files.name,
    settings: {
      ...(files.settings.rev ? { rev: files.settings.rev } : {}),
      slow_mode_seconds: files.settings.slow_mode_seconds,
      courses_label: files.settings.courses_label,
    },
    join_link_enabled: files.join_link_enabled,
    ...(files.join_link_rev ? { join_link_rev: files.join_link_rev } : {}),
    rooms: files.rooms.map((room) => ({
      key: room.key,
      ...(room.id ? { id: room.id } : {}),
      ...(room.rev ? { rev: room.rev } : {}),
      name: room.name,
      kind: room.kind,
      ...(room.archived ? { archived: true } : {}),
    })),
    courses: files.courses.map((course) => course.slug),
    ...(files.synced_courses ? { synced: { courses: files.synced_courses } } : {}),
  };
  return yaml.dump(doc, YAML_DUMP_OPTIONS);
}

/** `rules.md` as written: the text with one trailing newline; empty for none. */
export function renderRules(rules: string): string {
  const text = toLf(rules).replace(/\n+$/, '');
  return text === '' ? '' : `${text}\n`;
}

/** `community.yaml` and `rules.md` as written, by name. */
function renderCommunityFiles(files: CommunityFiles): Array<[string, string]> {
  return [
    [COMMUNITY_FILE, renderCommunityYaml(files)],
    [RULES_FILE, renderRules(files.rules)],
  ];
}

/** Write `community.yaml` and `rules.md` where they differ, no-follow. Returns the files written, by name. */
function writeCommunityFiles(root: string, folder: string, files: CommunityFiles): string[] {
  return renderCommunityFiles(files).flatMap(([name, content]) => (writeFileIfChangedNoFollow(root, path.join(folder, name), content) ? [name] : []));
}

/**
 * Push's save of `community.yaml` and `rules.md`: each file written, no-follow, only once what it
 * records differs from the files as read — so a push that records nothing in one leaves it as it
 * was, comments and line endings included.
 */
export function communityFilesSaver(root: string, folder: string, files: CommunityFiles): () => void {
  const saved = new Map(renderCommunityFiles(files));
  return () => {
    for (const [name, content] of renderCommunityFiles(files)) {
      if (saved.get(name) === content) continue;
      writeFileIfChangedNoFollow(root, path.join(folder, name), content);
      saved.set(name, content);
    }
  };
}

/**
 * Write the whole folder where it differs — `community.yaml`, `rules.md`, and every listed course's
 * `course.yaml` and lesson files — no-follow. Returns the files written, relative to the folder.
 * Creates the folder (a real directory under `root`) for a first pull.
 */
export function writeCommunityFolder(root: string, folder: string, files: CommunityFiles): string[] {
  return [...writeCommunityFiles(root, folder, files), ...files.courses.flatMap((course) => writeCourseFolder(root, folder, course))];
}
